/**
 * Phone helpers for SMS-compliant storage.
 *
 * Everything we persist is E.164 (+15551234567) so the number can be handed
 * straight to an SMS provider. Humans still see (555) 123-4567 while typing.
 */

/** Digits only, capped so pasted junk cannot blow past a real number. */
function digits(value: string): string {
  return value.replace(/\D+/g, "").slice(0, 15);
}

/** Progressive US formatting for what the person sees in the field. */
export function formatPhoneInput(value: string): string {
  const raw = value.trim();
  // Explicit international entry: leave it alone apart from stripping junk.
  if (raw.startsWith("+")) return `+${digits(raw)}`;

  let d = digits(raw);
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1);
  if (d.length > 10) return `+${digits(raw)}`;

  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
}

/**
 * Normalize to E.164, or null when the value is empty.
 * Returns `{ error }` when the value is present but not a textable number.
 */
export function normalizePhone(value: string | null | undefined): {
  e164: string | null;
  error?: string;
} {
  const raw = (value ?? "").trim();
  if (!raw) return { e164: null };

  const d = digits(raw);
  if (raw.startsWith("+")) {
    if (d.length < 8 || d.length > 15) return { e164: null, error: "Enter a valid phone number." };
    return { e164: `+${d}` };
  }

  const national = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  if (national.length !== 10 || /^[01]/.test(national)) {
    return {
      e164: null,
      error: "Enter a 10 digit US phone number, or start with + for international.",
    };
  }
  return { e164: `+1${national}` };
}

/** E.164 back to something readable in the UI and emails. */
export function formatPhoneDisplay(value: string | null | undefined): string {
  const raw = (value ?? "").trim();
  if (!raw) return "";
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(raw);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : raw;
}

/** Normalize or throw a user-facing error; convenient inside handlers. */
export function normalizePhoneOrThrow(value: string | null | undefined): string | null {
  const result = normalizePhone(value);
  if (result.error) throw new Error(result.error);
  return result.e164;
}
